import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  Plus, 
  Trash2, 
  Edit, 
  ExternalLink, 
  Clock, 
  Users,
  RefreshCw,
  GripVertical 
} from 'lucide-react';
import { starterRecipeAdminService, StarterRecipe } from '@/services/starterRecipe/starterRecipeAdminService';
import StarterRecipeImportDialog from './StarterRecipeImportDialog';
import StarterRecipeEditDialog from './StarterRecipeEditDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const StarterRecipesManager: React.FC = () => {
  const queryClient = useQueryClient();
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<StarterRecipe | null>(null);
  const [deletingRecipe, setDeletingRecipe] = useState<StarterRecipe | null>(null);

  // Fetch all starter recipes
  const { data: recipes, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-starter-recipes'],
    queryFn: () => starterRecipeAdminService.getAllStarterRecipes(),
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ recipeId, isActive }: { recipeId: string; isActive: boolean }) =>
      starterRecipeAdminService.toggleActive(recipeId, isActive),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-starter-recipes'] });
      toast.success(`Recipe ${data.is_active ? 'activated' : 'deactivated'}`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to toggle recipe: ${error.message}`);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (recipeId: string) => starterRecipeAdminService.deleteStarterRecipe(recipeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-starter-recipes'] });
      toast.success('Recipe deleted');
      setDeletingRecipe(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete recipe: ${error.message}`);
    },
  });

  const handleImportSuccess = () => {
    setIsImportOpen(false);
    queryClient.invalidateQueries({ queryKey: ['admin-starter-recipes'] });
  };

  const handleEditSuccess = () => {
    setEditingRecipe(null);
    queryClient.invalidateQueries({ queryKey: ['admin-starter-recipes'] });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <Skeleton className="h-40 w-full" />
              <CardContent className="pt-4">
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <div className="text-center">
            <p className="text-destructive font-medium">Error loading starter recipes</p>
            <p className="text-sm text-muted-foreground mt-1">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
            <Button variant="outline" className="mt-4" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const activeCount = recipes?.filter(r => r.is_active).length || 0;
  const inactiveCount = (recipes?.length || 0) - activeCount;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Starter Recipes</h2>
          <p className="text-muted-foreground text-sm mt-1">
            Curated recipes for new users to get started
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
            {activeCount} active
          </Badge>
          <Badge variant="outline" className="text-muted-foreground">
            {inactiveCount} inactive
          </Badge>
          <Button onClick={() => setIsImportOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Import Recipe
          </Button>
        </div>
      </div>

      {/* Recipe Grid */}
      {recipes && recipes.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {recipes.map((recipe) => (
            <Card 
              key={recipe.id} 
              className={`relative overflow-hidden transition-opacity ${
                !recipe.is_active ? 'opacity-60' : ''
              }`}
            >
              {/* Image or Placeholder */}
              <div className="aspect-video bg-muted relative">
                {recipe.image_url || recipe.storage_image_url ? (
                  <img
                    src={recipe.storage_image_url || recipe.image_url || ''}
                    alt={recipe.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                    No image
                  </div>
                )}
                {/* Status Badge */}
                <Badge 
                  className={`absolute top-2 right-2 ${
                    recipe.is_active 
                      ? 'bg-green-500 hover:bg-green-600' 
                      : 'bg-gray-500 hover:bg-gray-600'
                  }`}
                >
                  {recipe.is_active ? 'Active' : 'Inactive'}
                </Badge>
                {/* Display Order */}
                <Badge variant="secondary" className="absolute top-2 left-2">
                  <GripVertical className="h-3 w-3 mr-1" />
                  #{recipe.display_order}
                </Badge>
              </div>

              <CardHeader className="pb-2">
                <CardTitle className="text-lg line-clamp-1">{recipe.title}</CardTitle>
                {recipe.description && (
                  <CardDescription className="line-clamp-2">
                    {recipe.description}
                  </CardDescription>
                )}
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Recipe Info */}
                <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                  {recipe.total_time && (
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {recipe.total_time}
                    </div>
                  )}
                  {recipe.servings && (
                    <div className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {recipe.servings}
                    </div>
                  )}
                </div>

                {/* Tags */}
                {recipe.tags && recipe.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {recipe.tags.slice(0, 3).map((tag, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                    {recipe.tags.length > 3 && (
                      <Badge variant="secondary" className="text-xs">
                        +{recipe.tags.length - 3}
                      </Badge>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={recipe.is_active}
                      onCheckedChange={(checked) => 
                        toggleActiveMutation.mutate({ 
                          recipeId: recipe.id, 
                          isActive: checked 
                        })
                      }
                      disabled={toggleActiveMutation.isPending}
                    />
                    <span className="text-xs text-muted-foreground">
                      {recipe.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {recipe.source_url && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        asChild
                      >
                        <a href={recipe.source_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setEditingRecipe(recipe)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => setDeletingRecipe(recipe)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">
                No starter recipes yet. Import your first recipe from a URL.
              </p>
              <Button onClick={() => setIsImportOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Import Recipe
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import Dialog */}
      <StarterRecipeImportDialog
        open={isImportOpen}
        onOpenChange={setIsImportOpen}
        onSuccess={handleImportSuccess}
      />

      {/* Edit Dialog */}
      {editingRecipe && (
        <StarterRecipeEditDialog
          recipe={editingRecipe}
          open={!!editingRecipe}
          onOpenChange={(open) => !open && setEditingRecipe(null)}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingRecipe} onOpenChange={(open) => !open && setDeletingRecipe(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Starter Recipe</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingRecipe?.title}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => deletingRecipe && deleteMutation.mutate(deletingRecipe.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StarterRecipesManager;
